import React from 'react';
import { FiArrowRight, FiPlus, FiTrash2 } from 'react-icons/fi';
import { TbTargetArrow } from 'react-icons/tb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { cn } from '@/lib/utils';
import reforcoIcon from '@/assets/images/infantil/trilha-reforco.png';
import mapaIcon from '@/assets/images/infantil/trilha-mapa.png';
import { STATUS, STATUS_LABEL, TRAIL_PROGRESS_GRADIENT, formatDate, formatPercent, metaLineOf, progressLabelOf } from './trilha-list-utils';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

// Roxo fixo do tema lúdico em todos os tenants: `--color-secondary` muda de cor
// por tema (ex.: verde) e o card de criar trilha ficava destoando.
const PURPLE_BTN = {
  background: '#442B8C',
  boxShadow: '0 3px 0 #2D1A6E'
};

// Cores dos badges: tons "soft" fixos no lúdico (layout do Figma); nos demais
// temas, as variantes por token do tenant, no padrão das outras telas.
const BADGE = {
  playful: {
    personalized: 'info-soft',
    fixed: 'warning-soft',
    level: 'info',
    [STATUS.NOT_STARTED]: 'muted',
    [STATUS.IN_PROGRESS]: 'amber-soft',
    [STATUS.COMPLETED]: 'success-soft'
  },
  default: {
    personalized: 'primary-light',
    fixed: 'default',
    level: 'default',
    [STATUS.NOT_STARTED]: 'neutral-lightest',
    [STATUS.IN_PROGRESS]: 'warning',
    [STATUS.COMPLETED]: 'success'
  }
};

const useBadgeVariants = () => (usePlayfulStudent() ? BADGE.playful : BADGE.default);

function TrilhaTags({ row }) {
  const variants = useBadgeVariants();
  return (
    <>
      {row.personalized ? (
        <Badge
          variant={variants.personalized}
          size="sm"
        >
          Trilha Personalizada
        </Badge>
      ) : (
        <Badge
          variant={variants.fixed}
          size="sm"
        >
          Trilha fixa
        </Badge>
      )}
      {/* Trilha fixa não mostra status, como no layout */}
      {row.personalized && (
        <Badge
          variant={variants[row.status]}
          size="sm"
        >
          {STATUS_LABEL[row.status]}
        </Badge>
      )}
    </>
  );
}

// Nível da trilha (`streak.level` da API); sem atividade ainda não há nível.
function LevelBadge({ row }) {
  const variants = useBadgeVariants();
  if (!row.level) return null;
  return (
    <Badge
      variant={variants.level}
      size="sm"
    >
      {row.level.label}
    </Badge>
  );
}

export function TrilhaButton({ variant = 'continue', className, ...props }) {
  const playful = usePlayfulStudent();

  if (playful) {
    return (
      <PlayfulCardButton
        variant={variant}
        className={className}
        {...props}
      />
    );
  }

  return (
    <Button
      type="button"
      variant={variant === 'outline' ? 'outline' : 'default'}
      className={className}
      {...props}
    />
  );
}

function DeleteButton({ row, onDelete }) {
  return (
    <TrilhaButton
      variant="outline"
      aria-label={`Excluir ${row.title}`}
      onClick={() => onDelete(row)}
      className="h-9 w-9 shrink-0 p-0"
    >
      <FiTrash2 className="h-4 w-4" />
    </TrilhaButton>
  );
}

function ProgressBar({ percent, className, fillStyle }) {
  return (
    <div
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn('h-1.5 w-full overflow-hidden rounded-full', className)}
    >
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${percent}%`, ...fillStyle }}
      />
    </div>
  );
}

// Card grande da trilha acessada por último.
export function FeaturedTrilhaCard({ row, onContinue, onDelete }) {
  const playful = usePlayfulStudent();

  return (
    <article
      style={{ animation: `trilha-in 380ms ${EASE_OUT} both` }}
      // Lúdico: fundo creme, degradê no topo e borda laranja. Demais temas:
      // fundo branco e borda na cor primária do tenant.
      className={cn(
        'relative order-2 flex flex-col gap-4 overflow-hidden rounded-[20px] border-2 p-5 sm:flex-row sm:items-center sm:gap-6 sm:p-6 lg:order-none',
        playful ? 'border-[var(--color-mission-lighter,#FFA96B)] bg-[var(--color-bg-cream,#FFFAF5)]' : 'border-primary bg-white'
      )}
    >
      {playful && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-14"
          style={{ background: 'linear-gradient(180deg, rgba(214,253,255,0.55) 0%, transparent 100%)' }}
        />
      )}

      <img
        src={reforcoIcon}
        alt=""
        aria-hidden="true"
        className="relative h-[108px] w-[108px] shrink-0 self-start object-contain drop-shadow-sm sm:self-center"
      />

      <div className="relative flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="text-lg font-bold text-[#222731]">{row.title}</h3>
          <div className="flex flex-wrap gap-1.5">
            <LevelBadge row={row} />
            <TrilhaTags row={row} />
          </div>
        </div>
        <p className="mt-1 text-sm font-semibold text-[#222731]">{metaLineOf(row, { objectiveSeparator: ' ' })}</p>

        <div className="mt-3 flex items-center gap-3 sm:max-w-[300px]">
          <ProgressBar
            percent={row.percent}
            className="bg-white ring-1 ring-neutral-100"
            fillStyle={{ background: TRAIL_PROGRESS_GRADIENT }}
          />
          <span className="shrink-0 text-xs text-neutral-500">{formatPercent(row.percent)}</span>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <TrilhaButton
            onClick={onContinue}
            className="group/btn h-10 flex-1 py-0 sm:w-[170px] sm:flex-none"
          >
            Continuar
            <FiArrowRight className="h-4 w-4 transition-transform duration-150 group-hover/btn:translate-x-0.5" />
          </TrilhaButton>

          {row.deletable && (
            <div className="ml-auto">
              <DeleteButton
                row={row}
                onDelete={onDelete}
              />
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

export function CreateTrilhaCard({ onCreate }) {
  return (
    <article
      style={{ animation: `trilha-in 380ms ${EASE_OUT} 70ms both` }}
      className="order-1 flex flex-col justify-between gap-4 rounded-[20px] border-2 border-dashed border-[#7B63BF] bg-[var(--color-bg-lilac-soft,#F1ECFE)] p-5 lg:order-none"
    >
      <div className="flex items-center gap-4">
        <img
          src={mapaIcon}
          alt=""
          aria-hidden="true"
          className="h-[108px] w-[108px] shrink-0 object-contain drop-shadow-sm"
        />
        <div className="min-w-0">
          <h3 className="text-base font-bold text-[#222731]">Criar trilha personalizada</h3>
          <p className="mt-0.5 text-sm leading-snug text-[#222731]">Escolha o objetivo e as matérias. A trilha foca no que você precisa melhorar</p>
        </div>
      </div>

      <button
        type="button"
        onClick={onCreate}
        className="flex h-10 w-full items-center justify-center gap-2 rounded-full text-sm font-bold text-white transition-transform duration-100 active:scale-[0.97]"
        style={PURPLE_BTN}
      >
        <FiPlus className="h-4 w-4" />
        Criar trilha
      </button>
    </article>
  );
}

export function TrilhaRow({ row, index = 0, starting, onAccess, onDelete }) {
  const metaLine = metaLineOf(row);

  return (
    <article
      style={{ animation: `trilha-in 380ms ${EASE_OUT} ${150 + index * 70}ms both` }}
      className="grid grid-cols-1 gap-3 rounded-2xl border border-neutral-100 bg-white px-4 py-3 shadow-card md:grid-cols-[minmax(0,1.6fr)_minmax(0,0.9fr)_minmax(0,0.6fr)_minmax(0,1fr)_auto] md:items-center md:gap-6"
    >
      <div className="min-w-0">
        {/* Título trunca (nome completo no title) para os badges ficarem sempre na mesma linha */}
        <div className="flex min-w-0 items-center gap-1.5">
          <h3
            className="mr-1 min-w-0 truncate text-base font-bold text-[#222731]"
            title={row.title}
          >
            {row.title}
          </h3>
          <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
            <TrilhaTags row={row} />
          </span>
        </div>
        {metaLine && (
          <p className="mt-1 flex items-center gap-2 text-sm text-neutral-500">
            <TbTargetArrow
              aria-hidden="true"
              className="h-6 w-6 shrink-0 text-[var(--color-mission,#F99153)]"
            />
            <span className="truncate">{metaLine}</span>
          </p>
        )}
      </div>

      {/* Não iniciada: "-" na seção/missão e no último acesso, sem nível */}
      <p className="text-sm text-neutral-500 md:text-center">{progressLabelOf(row) || '-'}</p>

      <div className="flex text-sm text-neutral-500 md:justify-center">{row.level ? <LevelBadge row={row} /> : '-'}</div>

      <p className="text-sm text-neutral-500 md:text-center">{row.lastAccessAt ? `Último acesso em ${formatDate(row.lastAccessAt)}` : '-'}</p>

      <div className="flex items-center gap-3 md:justify-end">
        {row.comingSoon ? (
          <TrilhaButton
            variant="outline"
            disabled
            className="h-9 w-auto flex-1 px-5 py-0 md:flex-none"
          >
            Em breve
          </TrilhaButton>
        ) : (
          <TrilhaButton
            disabled={starting}
            onClick={() => onAccess(row)}
            className="h-9 w-auto flex-1 px-6 py-0 md:flex-none"
          >
            Acessar
          </TrilhaButton>
        )}

        {/* Lixeira só quando a API permite excluir (`deletable`); a coluna fica reservada pra alinhar as linhas */}
        {row.deletable ? (
          <DeleteButton
            row={row}
            onDelete={onDelete}
          />
        ) : (
          <span
            aria-hidden="true"
            className="hidden h-9 w-9 md:block"
          />
        )}
      </div>
    </article>
  );
}
